import type {
  PackageQuantitySource,
  ScopePackageItem,
} from "../domain/scopePackage";
import type { FeatureAction, FeatureGeometryBinding, QuantityUnit } from "../domain/feature";

/**
 * Package application planning (Phase 10, spec §21A) — pure and deterministic.
 *
 * Decides, for each package item, what applying it to THIS room would
 * actually do, before anything is written:
 *
 *  - reuse the feature that is already there (never a duplicate);
 *  - set an existing Design object to Replace (never a second toilet);
 *  - ask which object, when several could match (never guess destructively);
 *  - queue guided placement when the object does not exist yet;
 *  - create ordinary room-bound scope for everything non-spatial.
 *
 * Project type decides the ACTION, not a different package: new
 * construction installs what a renovation replaces (§16).
 *
 * No money is computed here — cost comes from the existing assembly engine
 * once quantities are known, so a package total is only ever the sum of the
 * project's own component estimates.
 */

export type PackageItemDisposition =
  /** Create new room-bound scope. */
  | "create"
  /** A feature for this definition already exists on the room — reuse it. */
  | "reuse-feature"
  /** An unscoped object is already in the plan — set its scope instead. */
  | "scope-existing-object"
  /** Spatial scope whose object must be placed in the Design. */
  | "needs-placement"
  /** Several existing objects could match — the user must choose. */
  | "ambiguous"
  /** Excluded by the checklist (or an unusable item). */
  | "skipped";

export interface ExistingFeatureRef {
  id: string;
  featureDefinitionId: string | null;
  name: string;
  designId: string | null;
}

export interface ExistingObjectRef {
  id: string;
  definitionId: string;
  /** Non-null = already carries scope; such an object is never re-scoped. */
  featureId: string | null;
  /** Non-null = copied from the Existing design (a real existing fixture). */
  originId: string | null;
  label: string;
}

export interface PackagePlanContext {
  projectType: "renovation" | "new_construction";
  roomId: string;
  roomName: string;
  roomTypeId: string | null;
  existingFeatures: ExistingFeatureRef[];
  existingObjects: ExistingObjectRef[];
  /** Geometry-derived room quantities, ×100 (null = not bound yet). */
  roomFloorAreaX100: number | null;
  roomPerimeterX100: number | null;
  /** Spec §89: gross wall surface (perimeter × wall height), ×100. */
  roomWallAreaX100?: number | null;
  /** True when that wall area used the assumed 8 ft height (nobody entered one). */
  roomWallHeightAssumed?: boolean;
  /**
   * Spec §89: the unit each item would be priced in (its assembly's driving
   * unit), so spatial work can be entered as a quantity instead of drawn.
   */
  itemUnits?: Map<string, QuantityUnit>;
  /** True when a Proposed design is available to place objects into. */
  hasProposedDesign: boolean;
  /** Display names so explanations read like the rest of the app. */
  featureDefinitionNames: Map<string, string>;
  objectDefinitionNames: Map<string, string>;
}

export interface PlannedItem {
  itemId: string;
  item: ScopePackageItem;
  included: boolean;
  disposition: PackageItemDisposition;
  /** Action after project-type resolution. */
  action: FeatureAction;
  /** Feature to reuse (disposition 'reuse-feature'). */
  existingFeatureId: string | null;
  /** Object to scope (disposition 'scope-existing-object'). */
  existingObjectId: string | null;
  /** Candidates when ambiguous — the user picks one. */
  candidateObjectIds: string[];
  /** Geometry binding to set, when the quantity comes from the room. */
  geometryBinding: FeatureGeometryBinding | null;
  /** Fixed quantity to set (manual source). */
  quantityX100: number | null;
  quantityUnit: QuantityUnit | null;
  /** The quantity this item will actually estimate with, when known. */
  effectiveQuantityX100: number | null;
  /** Plain sentence for the preview: what will happen and why. */
  explanation: string;
  /** Anything the user should know before applying (never silent). */
  warnings: string[];
}

export interface PackagePlan {
  items: PlannedItem[];
  /** Objects the user will place in the Design after applying. */
  placementCount: number;
  /** Items needing the user to choose which existing object they mean. */
  ambiguousCount: number;
  reusedCount: number;
  createdCount: number;
}

const BINDING_FOR_SOURCE: Record<Exclude<PackageQuantitySource, "default" | "manual">, FeatureGeometryBinding> = {
  "room-floor-area": "room-floor-area",
  "room-perimeter": "room-perimeter",
  "room-wall-area": "room-wall-area",
  "room-ceiling-area": "room-ceiling-area",
  "objects-count": "objects-count",
};

/**
 * Resolve the action for this project type. A renovation replaces what is
 * there; new construction installs it — the same package, read correctly,
 * never a fake Existing comparison (§16).
 */
export function resolveAction(item: ScopePackageItem, projectType: PackagePlanContext["projectType"], fallback: FeatureAction): FeatureAction {
  const wanted = item.action ?? fallback;
  if (projectType !== "new_construction") return wanted;
  return wanted === "replace" ? "install" : wanted;
}

export function planPackageApplication(
  items: ScopePackageItem[],
  context: PackagePlanContext,
  /** itemId → included; absent = the item's own default. */
  selections: Map<string, boolean>,
  /** itemId → the object the user picked when several matched. */
  objectChoices: Map<string, string> = new Map(),
  /** featureDefinitionId → its default action, for items that state none. */
  defaultActions: Map<string, FeatureAction> = new Map(),
  /**
   * Spec §89: itemId → a quantity the user typed in the preview, in the unit
   * the item is priced in. Spatial work entered this way is created as
   * ordinary scope with that quantity instead of waiting to be drawn.
   */
  quantityEntries: Map<string, number> = new Map(),
): PackagePlan {
  const planned: PlannedItem[] = [];
  // Each existing object may satisfy only ONE item.
  const claimedObjects = new Set<string>();
  const claimedFeatures = new Set<string>();

  for (const item of items) {
    const included = selections.get(item.id) ?? item.inclusion !== "default-off";
    const action = resolveAction(item, context.projectType, (item.featureDefinitionId && defaultActions.get(item.featureDefinitionId)) || "replace");
    const warnings: string[] = [];

    if (!included || item.itemType === "package") {
      planned.push(base(item, {
        included: false,
        disposition: "skipped",
        action,
        explanation: item.itemType === "package"
          ? "Composed packages are applied one room at a time — apply the child package to its own room."
          : "Not included.",
      }));
      continue;
    }

    // 1. Scope that already exists on this room is REUSED, never duplicated.
    const existingFeature = context.existingFeatures.find(
      (f) =>
        f.featureDefinitionId !== null &&
        f.featureDefinitionId === item.featureDefinitionId &&
        !claimedFeatures.has(f.id),
    );
    if (existingFeature) {
      claimedFeatures.add(existingFeature.id);
      planned.push(base(item, {
        included: true,
        disposition: "reuse-feature",
        action,
        existingFeatureId: existingFeature.id,
        explanation: `"${existingFeature.name}" is already on ${context.roomName} — the package reuses it instead of adding a second one.`,
        ...quantityPlan(item, context, warnings),
        warnings,
      }));
      continue;
    }

    // 2. Spatial items: an existing fixture becomes Replace, never a twin.
    if (item.objectDefinitionId !== null) {
      const typed = quantityEntries.get(item.id);
      const unit = context.itemUnits?.get(item.id) ?? null;
      const enteredPlan = (valueX100: number | null, explanation: string): PlannedItem =>
        base(item, {
          included: true,
          disposition: "create",
          action,
          explanation,
          geometryBinding: null,
          quantityX100: valueX100,
          quantityUnit: valueX100 === null ? null : unit,
          effectiveQuantityX100: valueX100,
          warnings,
        });
      if (!context.hasProposedDesign) {
        // SPEC §89 — DESIGN IS OPTIONAL. Nothing can be placed, so the work is
        // created as ordinary room scope priced from a quantity: a count
        // defaults to the package's own count; a run length or an area is
        // left for the user to enter, never invented.
        if (typed !== undefined && unit !== null) {
          planned.push(enteredPlan(typed, `Adds ${label(item, context)} to ${context.roomName} — ${formatQuantity(typed, unit)} entered.`));
        } else if (unit === "each") {
          const count = Math.max(1, item.objectCount) * 100;
          planned.push(enteredPlan(count, `Adds ${label(item, context)} to ${context.roomName} — ${formatQuantity(count, "each")}; change the count any time.`));
        } else {
          warnings.push(`Enter the ${unit === "lf" ? "run length (linear ft)" : unit === "sqft" ? "area (sq ft)" : "quantity"} to price it — no drawing needed.`);
          planned.push(enteredPlan(null, `Adds ${label(item, context)} to ${context.roomName}.`));
        }
        continue;
      }
      const chosen = objectChoices.get(item.id);
      const candidates = context.existingObjects.filter(
        (o) => o.definitionId === item.objectDefinitionId && o.featureId === null && !claimedObjects.has(o.id),
      );
      if (chosen && candidates.some((c) => c.id === chosen)) {
        claimedObjects.add(chosen);
        planned.push(base(item, {
          included: true,
          disposition: "scope-existing-object",
          action,
          existingObjectId: chosen,
          explanation: `The ${label(item, context)} already in ${context.roomName} will be set to ${actionWord(action)}.`,
          ...quantityPlan(item, context, warnings),
          warnings,
        }));
        continue;
      }
      if (candidates.length === 1) {
        claimedObjects.add(candidates[0].id);
        planned.push(base(item, {
          included: true,
          disposition: "scope-existing-object",
          action,
          existingObjectId: candidates[0].id,
          explanation: `${candidates[0].label} is already in ${context.roomName} — it will be set to ${actionWord(action)} rather than adding another.`,
          ...quantityPlan(item, context, warnings),
          warnings,
        }));
        continue;
      }
      if (candidates.length > 1) {
        planned.push(base(item, {
          included: true,
          disposition: "ambiguous",
          action,
          candidateObjectIds: candidates.map((c) => c.id),
          explanation: `${context.roomName} has ${candidates.length} of these already — choose which one this applies to.`,
          warnings,
        }));
        continue;
      }
      // A quantity typed in the preview means "I am not drawing this".
      if (typed !== undefined && unit !== null) {
        planned.push(enteredPlan(typed, `Adds ${label(item, context)} to ${context.roomName} — ${formatQuantity(typed, unit)} entered.`));
        continue;
      }
      planned.push(base(item, {
        included: true,
        disposition: "needs-placement",
        action,
        explanation: `${label(item, context)} will be placed in the Design (${item.objectCount} to place) — or enter a quantity instead.`,
        warnings,
      }));
      continue;
    }

    // 3. Ordinary room-bound scope.
    planned.push(base(item, {
      included: true,
      disposition: "create",
      action,
      explanation: `Adds ${label(item, context)} to ${context.roomName}.`,
      ...quantityPlan(item, context, warnings, quantityEntries.get(item.id)),
      warnings,
    }));
  }

  return {
    items: planned,
    placementCount: planned
      .filter((p) => p.disposition === "needs-placement")
      .reduce((sum, p) => sum + Math.max(1, p.item.objectCount), 0),
    ambiguousCount: planned.filter((p) => p.disposition === "ambiguous").length,
    reusedCount: planned.filter((p) => p.disposition === "reuse-feature" || p.disposition === "scope-existing-object").length,
    createdCount: planned.filter((p) => p.disposition === "create" || p.disposition === "needs-placement").length,
  };
}

/** Where this item's quantity comes from, and what it will be. */
function quantityPlan(
  item: ScopePackageItem,
  context: PackagePlanContext,
  warnings: string[],
  typed?: number,
): Pick<PlannedItem, "geometryBinding" | "quantityX100" | "quantityUnit" | "effectiveQuantityX100"> {
  // A typed quantity replaces a package's ASSUMED one ("30 sqft assumed");
  // room-measured quantities follow the room instead and are never typed over here.
  const typedUnit = item.quantityUnit ?? context.itemUnits?.get(item.id) ?? null;
  if (typed !== undefined && typedUnit !== null && (item.quantitySource === "manual" || item.quantitySource === "default")) {
    return { geometryBinding: null, quantityX100: typed, quantityUnit: typedUnit, effectiveQuantityX100: typed };
  }
  if (item.quantitySource === "manual") {
    return {
      geometryBinding: null,
      quantityX100: item.quantityX100,
      quantityUnit: item.quantityUnit,
      effectiveQuantityX100: item.quantityX100,
    };
  }
  if (
    item.quantitySource === "room-floor-area" ||
    item.quantitySource === "room-perimeter" ||
    item.quantitySource === "room-wall-area" ||
    item.quantitySource === "room-ceiling-area" ||
    item.quantitySource === "objects-count"
  ) {
    const binding = BINDING_FOR_SOURCE[item.quantitySource];
    const value =
      item.quantitySource === "room-floor-area" || item.quantitySource === "room-ceiling-area"
        ? context.roomFloorAreaX100
        : item.quantitySource === "room-perimeter"
          ? context.roomPerimeterX100
          : item.quantitySource === "room-wall-area"
            ? (context.roomWallAreaX100 ?? null)
            : null;
    if (item.quantitySource === "room-wall-area" && value !== null && context.roomWallHeightAssumed) {
      warnings.push("No wall height was entered for this room, so 8 ft walls are assumed.");
    }
    if (item.quantitySource !== "objects-count" && value === null) {
      warnings.push(
        "This room has no measurement yet, so the quantity stays blank until the room is drawn, its size is entered, or a quantity is typed.",
      );
    }
    return { geometryBinding: binding, quantityX100: null, quantityUnit: null, effectiveQuantityX100: value };
  }
  // 'default': spatial items count their objects; everything else is left
  // to the user rather than invented.
  return {
    geometryBinding: item.objectDefinitionId !== null ? "objects-count" : null,
    quantityX100: null,
    quantityUnit: null,
    effectiveQuantityX100: item.objectDefinitionId !== null ? Math.max(1, item.objectCount) * 100 : null,
  };
}

function base(item: ScopePackageItem, over: Partial<PlannedItem> & Pick<PlannedItem, "included" | "disposition" | "action" | "explanation">): PlannedItem {
  return {
    itemId: item.id,
    item,
    existingFeatureId: null,
    existingObjectId: null,
    candidateObjectIds: [],
    geometryBinding: null,
    quantityX100: null,
    quantityUnit: null,
    effectiveQuantityX100: null,
    warnings: [],
    ...over,
  };
}

function label(item: ScopePackageItem, context: PackagePlanContext): string {
  if (item.featureDefinitionId && context.featureDefinitionNames.has(item.featureDefinitionId)) {
    return context.featureDefinitionNames.get(item.featureDefinitionId)!;
  }
  if (item.objectDefinitionId && context.objectDefinitionNames.has(item.objectDefinitionId)) {
    return context.objectDefinitionNames.get(item.objectDefinitionId)!;
  }
  return "this scope";
}

function formatQuantity(valueX100: number, unit: QuantityUnit): string {
  const n = valueX100 % 100 === 0 ? String(valueX100 / 100) : (valueX100 / 100).toFixed(2);
  return `${n} ${unit === "lf" ? "linear ft" : unit === "sqft" ? "sq ft" : unit}`;
}

function actionWord(action: FeatureAction): string {
  return action.charAt(0).toUpperCase() + action.slice(1);
}
